import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveApiMediaUrl } from '../../api/client.js';

function ResilientImage({
  src,
  alt = '',
  className = '',
  style,
  loading = 'lazy',
  decoding = 'async',
  draggable = false,
  retries = 2,
  fallback = null,
}) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [src]);

  if (!src || failed) return fallback;

  const srcWithRetry =
    attempt > 0 ? `${src}${src.includes('?') ? '&' : '?'}img_retry=${attempt}` : src;

  return (
    <img
      src={srcWithRetry}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      decoding={decoding}
      draggable={draggable}
      onError={() => {
        setAttempt((prev) => {
          if (prev >= retries) {
            setFailed(true);
            return prev;
          }
          return prev + 1;
        });
      }}
    />
  );
}

function OminiaExitDoorIcon({ className = '', ...props }) {
  const uid = useId().replace(/:/g, '');
  const gFrame = `ominia-door-frame-${uid}`;
  const gPanel = `ominia-door-panel-${uid}`;
  const gKnob = `ominia-door-knob-${uid}`;
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden {...props}>
      <defs>
        <linearGradient id={gFrame} x1="4" y1="3" x2="20" y2="21" gradientUnits="userSpaceOnUse">
          <stop stopColor="hsl(var(--ominia-gradient-start))" />
          <stop offset="0.45" stopColor="hsl(var(--ominia-cyan))" />
          <stop offset="1" stopColor="hsl(var(--ominia-ia-mid))" />
        </linearGradient>
        <linearGradient id={gPanel} x1="8" y1="19" x2="16" y2="6" gradientUnits="userSpaceOnUse">
          <stop stopColor="hsl(var(--ominia-ia-start))" />
          <stop offset="1" stopColor="hsl(var(--ominia-ia-mid))" />
        </linearGradient>
        <radialGradient
          id={gKnob}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(15.25 11.75) rotate(52) scale(2.4)"
        >
          <stop stopColor="hsl(var(--ominia-cyan))" />
          <stop offset="1" stopColor="hsl(var(--ominia-gradient-start))" />
        </radialGradient>
      </defs>
      <rect
        x="5.15"
        y="2.85"
        width="13.7"
        height="18.3"
        rx="2.35"
        ry="2.35"
        stroke={`url(#${gFrame})`}
        strokeWidth="1.65"
      />
      <rect
        x="7.65"
        y="5.35"
        width="8.7"
        height="13.35"
        rx="1.15"
        ry="1.15"
        fill={`url(#${gPanel})`}
        fillOpacity="0.28"
        stroke={`url(#${gFrame})`}
        strokeWidth="1.05"
        strokeOpacity="0.9"
      />
      <path
        d="M9.2 7.85h5.6"
        stroke={`url(#${gFrame})`}
        strokeWidth="0.85"
        strokeLinecap="round"
        opacity="0.75"
      />
      <path
        d="M4.35 20.35h15.3"
        stroke={`url(#${gFrame})`}
        strokeWidth="1.35"
        strokeLinecap="round"
        opacity="0.88"
      />
      <circle cx="15.15" cy="11.9" r="1.38" fill={`url(#${gKnob})`} />
      <circle
        cx="15.15"
        cy="11.9"
        r="1.38"
        fill="none"
        stroke="hsl(var(--ominia-ia-start))"
        strokeOpacity="0.35"
        strokeWidth="0.4"
      />
    </svg>
  );
}

function SunIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/**
 * Cabeçalho público da vitrine (logo, nome, sessão, tema) — alinhado ao topo da `StorePage`.
 */
export function StorePublicHeader({
  slug,
  tenant,
  scheme,
  onToggleTheme,
  customerSession,
  onLogout,
  contaPageActive = false,
}) {
  const nome = tenant?.nome ? String(tenant.nome) : 'Catálogo';
  const logoUrl = tenant?.logo_url ? String(tenant.logo_url).trim() : '';

  return (
    <header
      data-ominia-store-header="1"
      className="sticky top-0 z-[35] border-b border-zinc-200/70 bg-white/75 backdrop-blur-xl dark:border-zinc-800/75 dark:bg-zinc-900/70"
    >
      <div className="mx-auto flex max-w-7xl flex-row items-center justify-between gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5">
        {slug ? (
          <Link
            to={`/${encodeURIComponent(slug)}`}
            className="group flex min-w-0 flex-1 items-center gap-2 rounded-lg py-0.5 outline-none transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[rgb(var(--st-rgb)/0.45)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900 sm:gap-3 sm:pr-1"
            aria-label="Voltar à vitrine da loja"
            title="Voltar à vitrine da loja"
          >
            {logoUrl ? (
              <ResilientImage
                src={resolveApiMediaUrl(logoUrl)}
                alt=""
                className="h-10 w-auto max-w-[4.5rem] shrink-0 object-contain transition group-hover:brightness-95 dark:group-hover:brightness-110 sm:h-[52px] sm:max-w-[182px] md:max-w-[234px]"
                loading="eager"
                retries={1}
              />
            ) : null}
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-400 dark:text-zinc-500 sm:text-[10px] sm:tracking-[0.28em]">
                Loja · {slug}
              </p>
              <p className="truncate font-display text-sm font-semibold leading-tight text-zinc-900 underline-offset-2 group-hover:underline dark:text-zinc-50 sm:text-lg">
                {nome}
              </p>
            </div>
          </Link>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            {logoUrl ? (
              <ResilientImage
                src={resolveApiMediaUrl(logoUrl)}
                alt=""
                className="h-10 w-auto max-w-[4.5rem] shrink-0 object-contain sm:h-[52px] sm:max-w-[182px]"
                loading="eager"
                retries={1}
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-400 dark:text-zinc-500 sm:text-[10px]">
                Catálogo
              </p>
              <p className="truncate font-display text-sm font-semibold leading-tight text-zinc-900 dark:text-zinc-50 sm:text-lg">
                {nome}
              </p>
            </div>
          </div>
        )}
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {customerSession ? (
            <div
              className="flex max-w-[min(10.5rem,38vw)] min-w-0 items-center gap-1 rounded-full border border-zinc-200/70 bg-zinc-50/90 px-1.5 py-0.5 shadow-sm dark:border-zinc-600/60 dark:bg-zinc-800/55 sm:max-w-[14rem] sm:gap-1.5 sm:px-2 sm:py-1"
              title={String(customerSession.nome || '').trim() || undefined}
            >
              <span className="min-w-0 flex-1 truncate text-left text-[10px] font-medium leading-tight text-zinc-700 dark:text-zinc-200 sm:text-[11px]">
                {String(customerSession.nome || '').trim() || 'Cliente'}
              </span>
              <Link
                to={`/${encodeURIComponent(slug)}/conta`}
                className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide underline-offset-2 hover:underline ${
                  contaPageActive
                    ? 'bg-[rgb(var(--st-rgb)/0.18)] text-[var(--st-700)] dark:text-[var(--st-400)]'
                    : 'text-[var(--st-700)] dark:text-[var(--st-400)]'
                }`}
                aria-current={contaPageActive ? 'page' : undefined}
              >
                Conta
              </Link>
              <button
                type="button"
                onClick={onLogout}
                aria-label="Sair da conta nesta loja"
                title="Sair"
                className="group/door flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-transparent transition hover:border-[rgb(var(--st-rgb)/0.25)] hover:bg-[rgb(var(--st-rgb)/0.08)] sm:h-8 sm:w-8 dark:hover:border-[rgb(var(--st-rgb)/0.3)] dark:hover:bg-[rgb(var(--st-rgb)/0.12)]"
              >
                <OminiaExitDoorIcon className="h-[1.15rem] w-[1.15rem] transition group-hover/door:scale-105 sm:h-[1.35rem] sm:w-[1.35rem]" />
              </button>
            </div>
          ) : null}
          <button
            type="button"
            onClick={onToggleTheme}
            aria-label={scheme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
            title={scheme === 'dark' ? 'Modo claro' : 'Modo escuro'}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:border-[rgb(var(--st-rgb)/0.4)] hover:bg-[rgb(var(--st-rgb)/0.08)] hover:text-[var(--st-700)] sm:h-10 sm:w-10 dark:border-zinc-600 dark:text-zinc-300 dark:hover:border-[rgb(var(--st-rgb)/0.35)] dark:hover:bg-zinc-800/90 dark:hover:text-[var(--st-400)]"
          >
            {scheme === 'dark' ? (
              <SunIcon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
            ) : (
              <MoonIcon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
            )}
          </button>
          <p className="hidden text-xs text-zinc-500 sm:block dark:text-zinc-400">
            tecnologia{' '}
            <Link className="font-display font-semibold" to="/">
              <span className="text-gradient">Omin</span>
              <span className="text-gradient-ia">IA</span>
            </Link>
          </p>
        </div>
      </div>
    </header>
  );
}
